import { useSyncExternalStore } from 'react';

/**
 * True only after the WHOLE app has mounted on the client. Starts false on the server
 * (renderToString never runs effects) AND on the client's very first render pass, so
 * any lazy()+Suspense content gated behind this hook is guaranteed to render nothing on
 * both sides of hydration - by construction, not by hoping the dynamic import() happens
 * to still be pending when hydrateRoot commits.
 *
 * Fixes a real crash (Monday board: BUG SEO SSR hydration crash-loop, React #418/#423/#321):
 * renderToString never waits for Suspense (docs/seo-ssr-search-pages-design.md §6 hazard
 * #6), so SSR always renders a lazy component's fallback. If the browser had already
 * loaded that component's chunk earlier in the same session, its import() resolves from
 * cache almost instantly - often before hydration's first commit - so the client tries to
 * render the real component where the server rendered nothing. Gating the lazy component
 * itself (not just its Suspense fallback) behind this hook removes that race.
 *
 * Backed by ONE shared external store (flipped once by markHydrated(), called from a
 * single effect at the App root - see App.jsx) rather than each call site owning its own
 * useState/useEffect pair. A page like /search/{slug} mounts dozens of ProductCards, each
 * of which used to run its own independent "am I hydrated yet" effect; under React's
 * concurrent/time-sliced hydration of a large tree, those effects can fire at different
 * moments relative to each other, so an early one could start loading its lazy chunk while
 * a sibling subtree elsewhere on the page is still mid-hydration - reproduced on real
 * search pages (React #418/#423/#321 cascades) but not in a low-latency local repro, where
 * everything resolves close enough together to mask the race. React fires effects
 * strictly bottom-up (children before parents) within a commit, so a single flip owned by
 * the root component is guaranteed to happen after every descendant's own mount effect in
 * that commit has already run, removing the per-instance timing variance entirely.
 */
let hydrated = false;
const listeners = new Set();

export function markHydrated() {
  if (hydrated) return;
  hydrated = true;
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return hydrated;
}

function getServerSnapshot() {
  return false;
}

export function useHydrated() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// Test-only: the module-level `hydrated` flag is a singleton shared across every test
// in the process (vite.config.js runs the suite with isolate:false), so without this,
// whichever test calls markHydrated() first would leave it true for every test after it.
export function __resetHydratedForTests() {
  hydrated = false;
}
